import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { AttachmentGrid } from "@/components/AttachmentPicker";
import { ArrowLeft, FileDown, Clock, MapPin, AlertTriangle, Check, Pencil, X, Share2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PATROL_STATUS_LABEL, PATROL_STATUS_COLOR, SCAN_STATUS_LABEL, VERIFICATION_MODE_LABEL, SCAN_METHOD_LABEL, formatDateTimeAR, formatTimeAR, splitDateTimeAR, isoFromARDateTime } from "@/lib/i18n";
import { Time24Select } from "@/components/Time24Select"; // Fix AW
import { isOperationalRole, roleLabel } from "@/lib/roles";

const dotIcon = (color) => L.divIcon({
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;background:${color};border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px ${color}"></div>`,
});
const POINT_ICON = dotIcon("#0A0A0A");
const SCAN_ICON = dotIcon("#BF3437");

const STATUS_BADGE = PATROL_STATUS_COLOR;

export default function PatrolDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [track, setTrack] = useState(null);
  const [vigiladores, setVigiladores] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ vigilador_id: "", date: "", time: "", tolerance_minutes: 60 }); // Fix AW: fecha + hora 24 hs
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data }, trackRes] = await Promise.all([
        api.get(`/patrols/${id}`),
        api.get(`/patrol-tracks?patrol_id=${id}`).catch(() => ({ data: { track: [], summary: null } })),
      ]);
      setP(data);
      setTrack(trackRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [id]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/users/roster"); setVigiladores((data || []).filter((x) => isOperationalRole(x.role) && x.active)); }
    catch {}
  })(); }, []);
  // Fix AW (2026-09-29): el lápiz del listado (?editar=1) abre directo "Cambiar horario".
  const wantsEdit = searchParams.get("editar") === "1";
  useEffect(() => {
    if (!wantsEdit || !p) return;
    if (p.status === "pendiente") {
      const { date, time } = splitDateTimeAR(p.scheduled_for);
      setEdit({ vigilador_id: p.vigilador_id || "", date, time, tolerance_minutes: p.tolerance_minutes || 60 });
      setEditOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [wantsEdit, p, setSearchParams]);

  if (!p) return <div style={{ padding: 24 }}><div className="skel" style={{ height: 30, width: 240 }} /></div>;

  const center = p.points.length ? [p.points[0].latitude, p.points[0].longitude] : [-34.6037, -58.3816];
  const geoScans = (p.scans || []).filter((s) => s.latitude != null && s.longitude != null);
  const scanLine = geoScans.map((s) => [s.latitude, s.longitude]);
  const gpsLine = (track?.track || []).map((t) => [t.latitude, t.longitude]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/patrols/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `ronda_${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Reporte descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareLink = async () => {
    try {
      const { data } = await api.post(`/patrols/${id}/verification-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success(`Link público copiado · válido ${data.expires_in_days} días`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = () => {
    // Fix AW (2026-09-29): fecha y hora argentinas, sin depender del reloj del dispositivo.
    const { date, time } = splitDateTimeAR(p.scheduled_for);
    setEdit({ vigilador_id: p.vigilador_id || "", date, time, tolerance_minutes: p.tolerance_minutes || 60 });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        scheduled_for: isoFromARDateTime(edit.date, edit.time), // Fix AW
        tolerance_minutes: parseInt(edit.tolerance_minutes),
      };
      if (edit.vigilador_id) payload.vigilador_id = edit.vigilador_id;
      await api.patch(`/patrols/${id}`, payload);
      toast.success("Ronda actualizada");
      setEditOpen(false); load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const cancelPatrol = async () => {
    if (!window.confirm("¿Cancelar esta ronda?")) return;
    try { await api.delete(`/patrols/${id}`); toast.success("Ronda cancelada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <Link to="/admin/rondas" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 16, textDecoration: "none", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700 }}>
        <ArrowLeft size={14} /> Rondas
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Ronda</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>{p.client_name}</h1>
          {/* Fix AU (2026-09-29): la ronda es de una sede: se muestra la sede y su dirección. */}
          {p.site_name && (
            <div style={{ fontSize: 15, fontWeight: 800, color: "#BF3437", margin: "0 0 6px" }} data-testid="patrol-site">
              Sede: {p.site_name}{p.site_address ? <span style={{ fontWeight: 500, color: "var(--invict-text-muted)" }}> · {p.site_address}</span> : null}
            </div>
          )}
          <div style={{ color: "var(--invict-text-muted)", fontSize: 13, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span><Clock size={12} style={{ display: "inline", marginRight: 4 }}/>{formatDateTimeAR(p.scheduled_for)}</span>
            <span>Vigilador: <b style={{ color: "#0A0A0A" }}>{p.vigilador_name}</b>{p.vigilador_deleted && <DeletedUserBadge id={p.id} />}{!p.vigilador_id && <span className="badge red" style={{ marginLeft: 6 }} data-testid="patrol-unassigned-badge">SIN ASIGNAR</span>}</span>
            {p.auto_generated && <span className="badge blue" data-testid="patrol-auto-badge">Ronda automática</span>}
            <span>{p.points_completed} / {p.total_points} puntos</span>
            {p.anomalies > 0 && <span style={{ color: "#BF3437" }}><AlertTriangle size={12} style={{ display: "inline", marginRight: 4 }}/>{p.anomalies} anomalía(s)</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`badge ${STATUS_BADGE[p.status] || "gray"}`} style={{ fontSize: 11, padding: "5px 12px" }}>{PATROL_STATUS_LABEL[p.status] || p.status}</span>
          {p.status === "pendiente" && <>
            <button className="btn-ghost" onClick={startEdit} data-testid="patrol-edit"><Pencil size={12} style={{ marginRight: 4 }}/>Cambiar horario / Reasignar</button>
            <button className="btn-ghost" onClick={cancelPatrol} style={{ color: "#BF3437", borderColor: "#BF3437" }} data-testid="patrol-cancel"><X size={12} style={{ marginRight: 4 }}/>Cancelar</button>
          </>}
          <button className="btn-ghost" onClick={shareLink} data-testid="patrol-share-link"><Share2 size={12} style={{ marginRight: 4 }}/>Copiar link público</button>
          <button className="btn-primary" onClick={downloadPdf} data-testid="patrol-pdf"><FileDown size={14} style={{ marginRight: 6 }} />Reporte PDF</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }} className="max-md:!grid-cols-1">
        <div className="card" style={{ padding: 0, overflow: "hidden", height: 460 }}>
          <MapContainer center={center} zoom={17} style={{ height: "100%", width: "100%" }} data-testid="patrol-map">
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {p.points.map((pt) => (
              <Marker key={pt.id} position={[pt.latitude, pt.longitude]} icon={POINT_ICON}>
                <Popup><b>{pt.name}</b><br/>Punto #{pt.order}<br/>Radio: {pt.radius_meters}m</Popup>
              </Marker>
            ))}
            {p.points.map((pt) => (
              <Circle key={`r-${pt.id}`} center={[pt.latitude, pt.longitude]} radius={pt.radius_meters} pathOptions={{ color: "#0A0A0A", fillColor: "#0A0A0A", fillOpacity: 0.05, weight: 1, dashArray: "4 4" }} />
            ))}
            {geoScans.map((s) => (
              <Marker key={s.id} position={[s.latitude, s.longitude]} icon={SCAN_ICON}>
                <Popup><b>{s.patrol_point_name}</b><br/>{formatTimeAR(s.scanned_at)}<br/>Distancia: {s.distance_meters}m<br/>Método: {SCAN_METHOD_LABEL[s.verification_method] || "QR"}<br/>Estado: {SCAN_STATUS_LABEL[s.status] || s.status}</Popup>
              </Marker>
            ))}
            {scanLine.length > 1 && <Polyline positions={scanLine} pathOptions={{ color: "#BF3437", weight: 3 }} />}
            {gpsLine.length > 1 && <Polyline positions={gpsLine} pathOptions={{ color: "#2563eb", weight: 3, dashArray: "2 6" }} />}
          </MapContainer>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 0, marginBottom: 8 }}>Recorrido GPS</h2>
          {track && track.summary && track.summary.count > 0 ? (
            <div data-testid="patrol-gps-summary" style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#2563eb", fontWeight: 700 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2563eb", display: "inline-block" }} /> Recorrido caminado (GPS)
              </span>
              <div>{track.summary.count} posiciones registradas · {(track.summary.distance_m / 1000).toFixed(2)} km aprox.</div>
            </div>
          ) : (
            <div data-testid="patrol-gps-summary-empty" style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 16 }}>
              Sin datos de GPS continuo para esta ronda todavía.
            </div>
          )}
          <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 0, marginBottom: 12 }}>Escaneos ({p.scans.length})</h2>
          {p.scans.length === 0 && <div style={{ color: "var(--invict-text-muted)", fontSize: 13, padding: "10px 0" }}>La ronda todavía no inició.</div>}
          {p.scans.map((s) => (
            <div key={s.id} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--invict-bone-3)" }}>
              <div style={{ width: 28, height: 28, background: s.status === "ok" ? "#16a34a" : "#BF3437", borderRadius: "50%", display: "grid", placeItems: "center", color: "white", flexShrink: 0 }}>
                {s.status === "ok" ? <Check size={14}/> : <AlertTriangle size={14}/>}
              </div>
              <div style={{ flex: 1, fontSize: 12 }}>
                <div style={{ fontWeight: 800, color: "#0A0A0A" }}>{s.patrol_point_name}</div>
                <div style={{ color: "var(--invict-text-muted)" }}>{formatTimeAR(s.scanned_at)} · {s.gps_degraded ? "sin señal GPS" : `${s.distance_meters}m`} · <span data-testid={`scan-method-${s.id}`}>{SCAN_METHOD_LABEL[s.verification_method] || "QR"}</span></div>
                {s.offline_sync && <span className="badge amber" style={{ marginTop: 4, marginRight: 4 }} data-testid={`scan-offline-${s.id}`}>Sincronizado sin conexión</span>}
                {s.gps_degraded && <span className="badge amber" style={{ marginTop: 4, marginRight: 4 }} data-testid={`scan-degraded-${s.id}`}>Verificación por foto/video</span>}
                {s.status !== "ok" && <span className="badge red" style={{ marginTop: 4 }}>{SCAN_STATUS_LABEL[s.status] || s.status}</span>}
                {s.attachments?.length > 0 && <AttachmentGrid items={s.attachments} />}
              </div>
            </div>
          ))}
          <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 22, marginBottom: 12 }}>Puntos del recorrido</h2>
          {p.points.map((pt) => (
            <div key={pt.id} style={{ display: "flex", gap: 8, padding: "6px 0", fontSize: 12 }}>
              <MapPin size={12} style={{ color: "#0A0A0A", marginTop: 2 }} />
              <div><b>#{pt.order} {pt.name}</b> <span style={{ fontSize: 9, fontWeight: 800, color: "var(--invict-text-muted)", textTransform: "uppercase" }}>{VERIFICATION_MODE_LABEL[pt.verification_mode || "qr"]}</span><div style={{ color: "var(--invict-text-muted)", fontSize: 10 }}>{pt.latitude.toFixed(5)}, {pt.longitude.toFixed(5)}</div></div>
            </div>
          ))}
        </div>
      </div>

      {editOpen && (
        <div className="modal-overlay" onClick={() => setEditOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitEdit}>
            <h3>Editar ronda</h3>
            <p>Solo se puede editar mientras esté pendiente.</p>
            <div className="field"><label>Vigilador asignado</label>
              <select value={edit.vigilador_id} onChange={(e) => setEdit({ ...edit, vigilador_id: e.target.value })} data-testid="edit-vigilador">
                <option value="">Sin asignar</option>
                {vigiladores.map((v) => <option key={v.id} value={v.id}>{v.name} · {roleLabel(v.role)}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div className="field"><label>Fecha</label><input type="date" required value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} data-testid="edit-schedule-date"/></div>
              <div className="field"><label>Hora (24 hs)</label><Time24Select required value={edit.time} onChange={(v) => setEdit({ ...edit, time: v })} testId="edit-schedule-time" /></div>
            </div>
            <div className="field"><label>Tolerancia (minutos)</label><input type="number" min="15" max="240" value={edit.tolerance_minutes} onChange={(e) => setEdit({ ...edit, tolerance_minutes: e.target.value })}/></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setEditOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="edit-save">{busy ? "Guardando..." : "Guardar"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
